import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { ArrowLeft, ArrowRight } from "lucide-react";
import KeyEventDisplay from "./KeyEventDisplay";
import { keyEvents, eventCategories } from "@/data/keyEventData";
import { cn } from "@/lib/utils";

interface KeyEventSectionProps {
  onOpenForm: () => void;
}

const KeyEventSection = ({ onOpenForm }: KeyEventSectionProps) => {
  const [activeCategory, setActiveCategory] = useState("seminaire");
  const [slideDirection, setSlideDirection] = useState<"left" | "right">("right");
  const isMobile = useIsMobile();

  const handleCategoryClick = (category: string) => {
    const currentIndex = eventCategories.findIndex(c => c.id === activeCategory);
    const newIndex = eventCategories.findIndex(c => c.id === category);
    setSlideDirection(newIndex > currentIndex ? "right" : "left");
    setActiveCategory(category);
  };

  const handleScrollLeft = () => {
    const currentIndex = eventCategories.findIndex(c => c.id === activeCategory);
    const prevIndex = (currentIndex - 1 + eventCategories.length) % eventCategories.length;
    setSlideDirection("left");
    setActiveCategory(eventCategories[prevIndex].id);
  };

  const handleScrollRight = () => {
    const currentIndex = eventCategories.findIndex(c => c.id === activeCategory);
    const nextIndex = (currentIndex + 1) % eventCategories.length;
    setSlideDirection("right");
    setActiveCategory(eventCategories[nextIndex].id);
  };

  // Category Navigation Component
  const CategoryNav = () => {
    if (isMobile) {
      return (
        <div className="w-full mb-6 px-4">
          <div className="grid grid-cols-2 gap-3">
            {eventCategories.map(category => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className={cn(
                  "text-sm font-normal py-2 px-3 rounded-full transition-colors border",
                  activeCategory === category.id 
                    ? "bg-primary text-primary-foreground border-primary" 
                    : "bg-background text-muted-foreground border-border hover:border-primary/50"
                )}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <div className="flex gap-8 mb-8">
        {eventCategories.map(category => (
          <button
            key={category.id}
            onClick={() => handleCategoryClick(category.id)}
            className={cn(
              "text-lg font-normal relative pb-2 transition-colors",
              activeCategory === category.id 
                ? "text-gray-900" 
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            {category.label}
            {activeCategory === category.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C6E278] rounded-full" />
            )}
          </button>
        ))}
      </div>
    );
  };

  return (
    <section className="py-16 bg-white relative">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center mb-8">
          <div className="text-center mb-6">
          <h2 className="text-4xl md:text-5xl font-normal text-gray-900">
            Votre événement clé en main
          </h2>
          </div>
          
          <CategoryNav />
        </div>

        <div className="relative">
          {!isMobile && (
            <>
              <button
                onClick={handleScrollLeft}
                className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all"
                aria-label="Précédent"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                onClick={handleScrollRight}
                className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all"
                aria-label="Suivant"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}
          
          <KeyEventDisplay
            filteredEvents={keyEvents.filter(e => e.category === activeCategory)}
            isMobile={isMobile}
            onOpenForm={onOpenForm}
            slideDirection={slideDirection}
            activeCategory={activeCategory}
          />
        </div>
        
        <div className="flex justify-center mt-10">
          <Button 
            onClick={onOpenForm}
            className="bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:shadow-xl hover:shadow-cta-green/30 font-semibold px-4 py-2 rounded-md transition-all duration-300 ease-out transform hover:scale-105"
          >
            Commencer gratuitement
          </Button>
        </div>
      </div>
    </section>
  );
};

export default KeyEventSection;
